/**
 * Service worker registration + update lifecycle (`pwa-contract.md` §4).
 *
 * T890 replaced the update banner with the switch on her next click. The two halves:
 *
 * - **Discovery.** The browser looks for a new `sw.js` only on a document navigation. A
 *   member who moves inside the app by clicking makes none, so a deploy was never even
 *   downloaded — the T890 diagnosis, reproduced with two local builds behind one origin.
 *   So the page asks: on each client navigation (`app/root.tsx`), and when she comes back
 *   to the tab or the app window. Throttled; `/sw.js` is excluded from the Pages Function,
 *   so a check costs no Function request.
 * - **The switch.** Once a new worker waits, the next navigation she starts inside the app
 *   becomes an ordinary page load of the page she asked for, after the worker takes over. A
 *   client navigation could never get there: it keeps the old bundle and the old worker.
 *   `app/hooks/use-update-switch.ts` asks `shouldSwitch` from React Router's blocker, so
 *   only a navigation the page really started is considered — a link whose own handler
 *   cancels it to ask a question (the auto-test's exit) keeps its question. Where a page
 *   load could cost her something a client navigation keeps — into the player, a form she
 *   typed into, the same page, a form being submitted — the navigation is left alone.
 *
 * Registration is explicit rather than auto-injected by `vite-plugin-pwa` because the
 * switch needs the `controlling` transition, which the injected snippet does not surface.
 *
 * Client-only. Every entry point no-ops during SSR and where service workers are absent,
 * so callers do not have to guard.
 */

import * as Sentry from "@sentry/react";
import { Workbox } from "workbox-window";
import { createEditTracker, switchHref } from "./update-switch";

export interface ServiceWorkerHandle {
  /** Ask the browser whether a newer worker was deployed. Throttled: safe on every navigation. */
  readonly checkForUpdate: () => void;
  /**
   * The URL to load as a document instead of the client navigation from the current page
   * to `to`, or `null` to let the navigation run. Only non-null while a new version waits
   * (or another tab already switched) and the rules of `update-switch.ts` allow it.
   */
  readonly shouldSwitch: (to: string) => string | null;
  /** Load `href` as a document under the new worker. `replace` keeps the history entry count. */
  readonly switchTo: (href: string, options: { replace: boolean }) => void;
}

/** At most one update check per tab per minute, however fast she clicks. */
const UPDATE_CHECK_INTERVAL_MS = 60_000;

/**
 * How long her navigation waits for the new worker before the page loads anyway (T560's lesson:
 * an event that may never arrive needs a deadline). Activation takes milliseconds when it
 * works; when another tab still holds the old worker it may not happen at all, and the
 * page then loads under the old one — her click still goes where she asked, and the next
 * one tries again. Short, because she is waiting on a link, not on a button that says so.
 */
const SWITCH_DEADLINE_MS = 3_000;

/** One registration per document: the root effect that calls this runs on every client navigation. */
let registered: ServiceWorkerHandle | null = null;

function supported(): boolean {
  // The dev server builds no `sw.js`: registering there only logs a 404 on every page
  // (found by the T972 browser tester). Production and preview builds register as before.
  if (import.meta.env.MODE === "development") return false;
  return typeof window !== "undefined" && "serviceWorker" in navigator;
}

export function registerServiceWorker(): ServiceWorkerHandle | null {
  if (!supported()) return null;
  if (registered) return registered;

  const wb = new Workbox("/sw.js");
  let registration: ServiceWorkerRegistration | undefined;
  let lastCheck = Number.NEGATIVE_INFINITY;

  /**
   * True once a worker other than the one this document started under took control —
   * another tab switched, so this page runs the old bundle under the new worker. Its next
   * click needs a page load and no hand-over. A FIRST install does not count: the worker
   * calls `clients.claim()` on activate, so `controlling` fires with no previous controller
   * and nothing to update to (T52, R-T52-4).
   */
  let controlled = Boolean(navigator.serviceWorker.controller);
  let workerReplaced = false;

  /** Set only by a navigation she started. `controlling` navigates nowhere without it (R-T52-4). */
  let target: { href: string; replace: boolean } | null = null;
  let deadline: ReturnType<typeof setTimeout> | null = null;
  let leaving = false;

  const leave = (href: string, replace: boolean) => {
    if (leaving) return;
    leaving = true;
    if (deadline !== null) clearTimeout(deadline);
    if (replace) window.location.replace(href);
    else window.location.assign(href);
  };

  wb.addEventListener("controlling", () => {
    if (target !== null) {
      leave(target.href, target.replace);
      return;
    }
    if (controlled) workerReplaced = true;
    controlled = true;
  });

  const checkForUpdate = () => {
    const now = Date.now();
    if (now - lastCheck < UPDATE_CHECK_INTERVAL_MS) return;
    lastCheck = now;
    // Offline, or a deploy mid-flight answering 404: the next check retries. Nothing for her
    // to see or do, so nothing to report.
    wb.update().catch(() => undefined);
  };

  // An installed app window regains focus without always becoming "visible" again (a
  // desktop window behind another one is still visible), so focus asks too. Throttled.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") checkForUpdate();
  });
  window.addEventListener("focus", checkForUpdate);

  const edits = createEditTracker();
  document.addEventListener(
    "input",
    (event) => {
      // The live element, not a copy: `isConnected` must be read when she navigates.
      const field = event.target as { isConnected?: unknown; form?: unknown } | null;
      if (field && typeof field.isConnected === "boolean" && "form" in field) {
        edits.edited(field as { isConnected: boolean; form: unknown });
      }
    },
    true,
  );

  /**
   * A form submission is a navigation too, and React Router's blocker cannot tell it from a
   * link (it gets locations only). Loading its target as a document would drop the POST, so
   * a navigation that starts inside a native `submit` event is never switched. React
   * Router's `<Form>` calls `navigate()` synchronously from that event, before any await.
   */
  let submitting = false;
  document.addEventListener(
    "submit",
    () => {
      submitting = true;
      setTimeout(() => {
        submitting = false;
      }, 0);
    },
    true,
  );

  /**
   * Back to a page the switch already left, restored from the back-forward cache: its
   * module state still says "leaving", which would hold every later navigation, and it runs
   * the OLD bundle — under the new worker, or under the old one with the new still waiting.
   * Start over as a page another tab switched: its next navigation loads a document (infra
   * audit rounds 1–2). The router's own blocker is reset by `use-update-switch.ts`.
   */
  window.addEventListener("pageshow", (event) => {
    if (!(event as PageTransitionEvent).persisted) return;
    if (leaving || target !== null) workerReplaced = true;
    leaving = false;
    target = null;
    if (deadline !== null) clearTimeout(deadline);
    deadline = null;
  });

  const shouldSwitch = (to: string): string | null => {
    if (leaving || target !== null) return to;
    if (!registration?.waiting && !workerReplaced) return null;
    if (submitting) return null;
    return switchHref({ from: window.location.href, to, hasUnsavedInput: edits.hasUnsavedInput() });
  };

  const switchTo = (href: string, { replace }: { replace: boolean }) => {
    // A second navigation during the hand-over: the page she asked for first is on its way.
    if (leaving || target !== null) return;
    if (!registration?.waiting) {
      leave(href, replace);
      return;
    }
    target = { href, replace };
    // Armed BEFORE asking, as in T560: whatever happens to the message, her page loads.
    deadline = setTimeout(() => leave(href, replace), SWITCH_DEADLINE_MS);
    try {
      wb.messageSkipWaiting();
    } catch {
      leave(href, replace);
    }
  };

  /**
   * T771 — the rejection is ours to handle, and there is nobody to tell.
   *
   * `register()` can reject outside our control: a browser without storage for the
   * worker, a privacy mode, or — the case Sentry reported on 2026-09-17
   * (JAVASCRIPT-REACT-2M) — an injected script on an Android device that overrides
   * `navigator.serviceWorker.register` and refuses. A discarded promise (`void`) does not
   * handle that rejection, so it surfaced as `onunhandledrejection` and mailed the founder
   * about a failure the member cannot see: the app runs without the worker; what she loses
   * is installability and the offline shell, and nothing she could do would change that.
   * Same best-effort discipline as `requestBackgroundSync` below — with one difference the
   * review asked for: the reason is kept as a Sentry breadcrumb, so a systemic failure (a
   * missing `sw.js` after a bad deploy) is still readable on the next event that does fire,
   * without costing an event or a mail of its own. A no-op wherever the SDK is not
   * initialised (localhost).
   */
  wb.register()
    .then((result) => {
      registration = result;
    })
    .catch((error: unknown) => {
      Sentry.addBreadcrumb({
        category: "pwa",
        level: "warning",
        message: `service worker registration failed: ${String(error).slice(0, 200)}`,
      });
    });

  registered = { checkForUpdate, shouldSwitch, switchTo };
  return registered;
}

/**
 * Ask for a Background Sync slot so the queue can drain with no tab open.
 *
 * Best-effort by design: iOS Safari has no Background Sync at all, and iOS is a
 * first-class target here. The window-side `online` drain is the mechanism; this is the
 * bonus. Failure is silent because there is nothing for the member to do about it.
 */
export async function requestBackgroundSync(tag: string): Promise<boolean> {
  if (!supported()) return false;
  try {
    const registration = (await navigator.serviceWorker.ready) as ServiceWorkerRegistration & {
      sync?: { register: (tag: string) => Promise<void> };
    };
    if (!registration.sync) return false;
    await registration.sync.register(tag);
    return true;
  } catch {
    return false;
  }
}
